import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import { Separator } from '@qr-front/ui/components/Separator'
import * as Accordion from '@radix-ui/react-accordion'
import { SectionIcon } from 'features/generator/qrTypes/VCard/client'
import { AnimatePresence, motion } from 'motion/react'
import { ReactElement, useContext, useEffect, useRef } from 'react'

import { TSvgIcon } from '@/types'

import { IsPreviewModeContext } from '../BusinessPageScreenContexts'

export const AccordionItem = ({
	title,
	Icon,
	content,
	isOpen,
}: {
	title: string
	Icon: TSvgIcon
	content: ReactElement
	isOpen: boolean
}) => {
	const sectionRef = useRef<HTMLDivElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)
	const isPreviewMode = useContext(IsPreviewModeContext)

	useEffect(() => {
		if (isOpen && sectionRef.current && contentRef.current && !isPreviewMode) {
			const sectionElement = sectionRef.current
			const contentElement = contentRef.current

			setTimeout(() => {
				const contentHeight = contentElement.scrollHeight
				const viewportHeight = window.innerHeight
				if (contentHeight > viewportHeight * 0.4) {
					sectionElement.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
					})
				} else {
					sectionElement.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					})
				}
			}, 200)
		}
	}, [isOpen, isPreviewMode])

	return (
		<Accordion.Item
			key={title}
			value={title}
			className="group rounded-6 bg-white shadow-generator"
			ref={sectionRef}
		>
			<Accordion.Trigger className="type-body-small flex w-full items-center justify-between gap-2 p-2 transition-colors">
				<div className="flex items-center gap-2 text-left">
					<SectionIcon Icon={Icon} />
					{title}
				</div>
				<LineChevronDownSmall className="size-4 text-grey" />
			</Accordion.Trigger>
			<Separator
				collapse
				className="ml-2 w-[calc(100%_-_theme(spacing.4))] transition-opacity group-data-[state=closed]:opacity-0"
			/>
			<Accordion.Content forceMount>
				<AnimatePresence initial={false}>
					{isOpen ? (
						<motion.div
							key={title}
							initial={{ height: 0 }}
							animate={{ height: 'auto' }}
							exit={{ height: 0 }}
							className="overflow-hidden"
							ref={contentRef}
						>
							<div className="p-4">{content}</div>
						</motion.div>
					) : null}
				</AnimatePresence>
			</Accordion.Content>
		</Accordion.Item>
	)
}
